import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select as MuiSelect,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Grid
} from '@mui/material';
// Removed dnd-kit imports - using native mouse events for drag-to-create
import { format, startOfDay, setHours, setMinutes, differenceInMinutes, parseISO, isSameDay, addMinutes, addDays } from 'date-fns';
import TimesheetViewControls, { getDateRangeFromScale, getStartEndDateStrings } from './TimesheetViewControls';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { getUserTimezone, getUserClock24h, formatTimeOnly } from '../utils/dateUtils';
import { useUserPreferences } from '../hooks/useUserPreferences';
import api from '../services/api';

export type CalendarViewScale = '1-day' | '3-day' | 'week';
export type TimeResolution = '1-min' | '5-min' | '10-min' | '15-min' | '30-min' | '1-hour';

interface TimesheetEntry {
  id: string;
  organizationId: string;
  userId: string;
  taskId: string;
  startTime?: string;
  stopTime?: string;
  hours?: number;
  description?: string;
  approvalStatus: string;
  task?: {
    id: string;
    name: string;
    taskNumber?: string;
    isActive?: boolean;
    account?: {
      id: string;
      name: string;
      color?: string;
      company?: {
        id: string;
        name: string;
      };
    };
  };
}

interface TimesheetCalendarProps {
  organizationId: string;
  /** @deprecated Calendar fetches its own date-scoped entries; ignored if provided. */
  entries?: any[];
  onEntryCreate?: (entry: any) => Promise<void>;
  onEntryUpdate?: (id: string, entry: any) => Promise<void>;
  onEntryDelete?: (id: string) => Promise<void>;
  loading?: boolean;
}

interface TimeSlot {
  time: Date;
  displayTime: string;
}

interface CalendarEntryBlock {
  entry: any; // Use any to match API structure
  startSlot: number;
  endSlot: number;
  column: number;
  color: string;
}

const TIME_RESOLUTIONS: { value: TimeResolution; label: string; minutes: number }[] = [
  { value: '1-min', label: '1 min', minutes: 1 },
  { value: '5-min', label: '5 min', minutes: 5 },
  { value: '10-min', label: '10 min', minutes: 10 },
  { value: '15-min', label: '15 min', minutes: 15 },
  { value: '30-min', label: '30 min', minutes: 30 },
  { value: '1-hour', label: '1 hour', minutes: 60 }
];

const SLOT_HEIGHT = 40; // Fixed height per slot in pixels

// Generate color from account name hash
const generateColorFromHash = (accountName: string): string => {
  let hash = 0;
  for (let i = 0; i < accountName.length; i++) {
    hash = accountName.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate HSL color with minimum brightness
  const hue = Math.abs(hash) % 360;
  const saturation = 60 + (Math.abs(hash) % 20); // 60-80%
  const lightness = 50 + (Math.abs(hash) % 20); // 50-70% (minimum brightness)
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Get color for entry (account-based only)
const getEntryColor = (entry: any): string => {
  const account = entry.task?.account;
  if (!account) return '#9e9e9e'; // Default gray
  
  // Use custom color if available, otherwise generate from hash
  if (account.color) {
    return account.color;
  }
  
  return generateColorFromHash(account.name);
};

// Calculate relative luminance for WCAG contrast calculation
const getLuminance = (r: number, g: number, b: number): number => {
  // Convert RGB to relative luminance using WCAG formula
  const [rs, gs, bs] = [r, g, b].map(val => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// Calculate contrast ratio between two colors (WCAG formula)
const getContrastRatio = (lum1: number, lum2: number): number => {
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
};

// Calculate optimal text color (white or black) based on background color using WCAG contrast
const getContrastColor = (backgroundColor: string): string => {
  // Parse color (supports hex, rgb, hsl)
  let r = 0, g = 0, b = 0;
  
  if (backgroundColor.startsWith('#')) {
    // Hex color
    const hex = backgroundColor.slice(1);
    if (hex.length === 3) {
      // Short hex format (#RGB -> #RRGGBB)
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else {
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    }
  } else if (backgroundColor.startsWith('rgb')) {
    // RGB color
    const matches = backgroundColor.match(/\d+/g);
    if (matches && matches.length >= 3) {
      r = parseInt(matches[0]);
      g = parseInt(matches[1]);
      b = parseInt(matches[2]);
    }
  } else if (backgroundColor.startsWith('hsl')) {
    // HSL color - convert to RGB
    const matches = backgroundColor.match(/\d+/g);
    if (matches && matches.length >= 3) {
      const h = parseInt(matches[0]) / 360;
      const s = parseInt(matches[1]) / 100;
      const l = parseInt(matches[2]) / 100;
      
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
      const m = l - c / 2;
      
      let r1 = 0, g1 = 0, b1 = 0;
      if (h < 1/6) { r1 = c; g1 = x; b1 = 0; }
      else if (h < 2/6) { r1 = x; g1 = c; b1 = 0; }
      else if (h < 3/6) { r1 = 0; g1 = c; b1 = x; }
      else if (h < 4/6) { r1 = 0; g1 = x; b1 = c; }
      else if (h < 5/6) { r1 = x; g1 = 0; b1 = c; }
      else { r1 = c; g1 = 0; b1 = x; }
      
      r = Math.round((r1 + m) * 255);
      g = Math.round((g1 + m) * 255);
      b = Math.round((b1 + m) * 255);
    }
  }
  
  // Calculate luminance for background
  const bgLuminance = getLuminance(r, g, b);
  
  // Calculate contrast ratio for white text (#FFFFFF)
  const whiteLuminance = getLuminance(255, 255, 255);
  const whiteContrast = getContrastRatio(bgLuminance, whiteLuminance);
  
  // Calculate contrast ratio for black text (#000000)
  const blackLuminance = getLuminance(0, 0, 0);
  const blackContrast = getContrastRatio(bgLuminance, blackLuminance);
  
  // Return the color with better contrast (higher ratio is better)
  // Minimum 4.5:1 for normal text, but we'll use whichever is better
  return whiteContrast > blackContrast ? '#FFFFFF' : '#000000';
};

// Format hours for display (e.g., "4h", "1.5h")
const formatHours = (hours: number | undefined | string | null): string => {
  // Convert to number if it's a string or handle null/undefined
  const numHours = typeof hours === 'string' ? parseFloat(hours) : (hours ?? 0);
  
  // Ensure it's a valid number
  if (isNaN(numHours) || numHours === 0) return '0h';
  
  // Check if it's a whole number
  if (numHours % 1 === 0) return `${numHours}h`;
  
  // Format with one decimal place
  return `${numHours.toFixed(1)}h`;
};

type LocalDateKey = string; // YYYY-MM-DD in the user's timezone

const dateKeyFormatters = new Map<string, Intl.DateTimeFormat>();
const timePartFormatters = new Map<string, Intl.DateTimeFormat>();

const getDateKeyFormatter = (timeZone: string) => {
  let formatter = dateKeyFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    dateKeyFormatters.set(timeZone, formatter);
  }
  return formatter;
};

const getTimePartFormatter = (timeZone: string) => {
  let formatter = timePartFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    timePartFormatters.set(timeZone, formatter);
  }
  return formatter;
};

const getLocalDateKey = (date: Date, timeZone: string): LocalDateKey =>
  getDateKeyFormatter(timeZone).format(date);

const getLocalMinutesFromMidnight = (date: Date, timeZone: string): number => {
  const parts = getTimePartFormatter(timeZone).formatToParts(date);
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
  const minute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);
  return hour * 60 + minute;
};

const localDateKeyToUTCDate = (key: LocalDateKey): Date => {
  const [y, m, d] = key.split('-').map((v) => parseInt(v, 10));
  return new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0));
};

const utcDateToLocalDateKey = (date: Date): LocalDateKey => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Format time based on user's 24/12-hour clock preference
const formatTime = (date: Date, clock24h: boolean): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  
  if (clock24h) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } else {
    const displayHours = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }
};

const getZonedDateParts = (date: Date, timezone: string) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  const parts = formatter.formatToParts(date);
  const getPart = (type: string) => parts.find((p) => p.type === type)?.value || '0';
  return {
    year: parseInt(getPart('year'), 10),
    month: parseInt(getPart('month'), 10),
    day: parseInt(getPart('day'), 10),
    hour: parseInt(getPart('hour'), 10),
    minute: parseInt(getPart('minute'), 10)
  };
};

const utcIsoToFormFields = (isoString: string, timezone: string) => {
  const date = new Date(isoString);
  const parts = getZonedDateParts(date, timezone);
  return {
    date: `${parts.year.toString().padStart(4, '0')}-${parts.month.toString().padStart(2, '0')}-${parts.day.toString().padStart(2, '0')}`,
    time: `${parts.hour.toString().padStart(2, '0')}:${parts.minute.toString().padStart(2, '0')}`
  };
};

const zonedDateTimeToUtcDate = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timezone: string
): Date => {
  let utcMs = Date.UTC(year, month - 1, day, hour, minute, 0, 0);

  // Resolve timezone offset by converging formatted local parts to desired local parts.
  for (let i = 0; i < 4; i++) {
    const candidate = new Date(utcMs);
    const actual = getZonedDateParts(candidate, timezone);
    const desiredLocalMs = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
    const actualLocalMs = Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, 0, 0);
    const diffMs = desiredLocalMs - actualLocalMs;
    if (diffMs === 0) break;
    utcMs += diffMs;
  }

  return new Date(utcMs);
};

const formFieldsToUtcDate = (dateString: string, timeString: string, timezone: string): Date | null => {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeString);
  if (!dateMatch || !timeMatch) return null;

  const year = parseInt(dateMatch[1], 10);
  const month = parseInt(dateMatch[2], 10);
  const day = parseInt(dateMatch[3], 10);
  const hour = parseInt(timeMatch[1], 10);
  const minute = parseInt(timeMatch[2], 10);
  if (
    Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day) ||
    Number.isNaN(hour) || Number.isNaN(minute)
  ) {
    return null;
  }

  return zonedDateTimeToUtcDate(year, month, day, hour, minute, timezone);
};

// Convert 24-hour time string (HH:mm) to 12-hour format (hh:mm AM/PM)
const convert24To12 = (time24: string): string => {
  if (!time24 || !time24.includes(':')) return time24 || '';
  // Check if already in 12-hour format (has AM/PM)
  if (time24.toUpperCase().includes('AM') || time24.toUpperCase().includes('PM')) {
    return time24;
  }
  const [hours, minutes] = time24.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time24;
  const displayHours = hours % 12 || 12;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};

// Convert 12-hour time string (hh:mm AM/PM) to 24-hour format (HH:mm)
const convert12To24 = (time12: string): string => {
  if (!time12) return '';
  // Remove extra spaces and normalize
  const normalized = time12.trim().toUpperCase();
  const match = normalized.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/);
  if (!match) {
    // If it's already in 24-hour format, return as is
    if (normalized.match(/^\d{2}:\d{2}$/)) return normalized;
    return '';
  }
  const [, hoursStr, minutesStr, ampm] = match;
  let hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  
  if (ampm === 'PM' && hours !== 12) {
    hours += 12;
  } else if (ampm === 'AM' && hours === 12) {
    hours = 0;
  }
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Build metadata line: [Task Name XXh] | [Account] | [Company] | [Organization]
const buildMetadataLine = (entry: any): string => {
  const parts: string[] = [];
  
  // Task name with hours
  const taskName = entry.task?.name || 'Unknown';
  const hours = formatHours(entry.hours);
  parts.push(`${taskName} ${hours}`);
  
  // Account
  if (entry.task?.account?.name) {
    parts.push(entry.task.account.name);
  }
  
  // Company (only if multiple organizations exist - we'll show it for now)
  if (entry.task?.account?.company?.name) {
    parts.push(entry.task.account.company.name);
  }
  
  // Organization (only if multiple organizations exist - we'll show it for now)
  if (entry.organization?.name) {
    parts.push(entry.organization.name);
  }
  
  return parts.join(' | ');
};

// Calculate overlap layout (column-based algorithm)
const calculateOverlapLayout = (
  dayEntries: any[],
  timeSlotsLength: number,
  timezone: string,
  timeResolution: TimeResolution
): CalendarEntryBlock[] => {
  if (!dayEntries.length || timeSlotsLength <= 0) return [];

  const resolution = TIME_RESOLUTIONS.find(r => r.value === timeResolution)?.minutes || 15;
  const timeFormatter = getTimePartFormatter(timezone);

  const blocks: CalendarEntryBlock[] = [];
  for (const entry of dayEntries) {
    if (!entry.startTime || !entry.stopTime) continue;

    const start = parseISO(entry.startTime);
    const stop = parseISO(entry.stopTime);
    const startParts = timeFormatter.formatToParts(start);
    const stopParts = timeFormatter.formatToParts(stop);

    const startHours = parseInt(startParts.find(p => p.type === 'hour')?.value || '0', 10);
    const startMinutes = parseInt(startParts.find(p => p.type === 'minute')?.value || '0', 10);
    const stopHours = parseInt(stopParts.find(p => p.type === 'hour')?.value || '0', 10);
    const stopMinutes = parseInt(stopParts.find(p => p.type === 'minute')?.value || '0', 10);

    const startSlot = Math.floor((startHours * 60 + startMinutes) / resolution);
    const endSlot = Math.ceil((stopHours * 60 + stopMinutes) / resolution);

    blocks.push({
      entry,
      startSlot: Math.max(0, startSlot),
      endSlot: Math.min(timeSlotsLength, Math.max(startSlot + 1, endSlot)),
      column: 0,
      color: getEntryColor(entry)
    });
  }

  if (blocks.length === 0) return [];

  blocks.sort((a, b) => a.startSlot - b.startSlot || a.endSlot - b.endSlot);
  const columnEnds: number[] = [];
  for (const block of blocks) {
    let col = columnEnds.findIndex((end) => end <= block.startSlot);
    if (col === -1) {
      col = columnEnds.length;
      columnEnds.push(block.endSlot);
    } else {
      columnEnds[col] = block.endSlot;
    }
    block.column = col;
  }

  return blocks;
};

const TimesheetCalendar: React.FC<TimesheetCalendarProps> = ({
  organizationId,
  onEntryCreate,
  onEntryUpdate,
  onEntryDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 768px
  const {
    preferences,
    loading: preferencesLoading,
    hydrated: preferencesHydrated,
    getPreference,
    setPreference,
  } = useUserPreferences();
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<any[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // State - use getPreference directly with try-catch in initializers
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewScale, setViewScale] = useState<CalendarViewScale>(() => {
    if (isMobile) return '1-day';
    try {
      return (getPreference('timesheet.calendar.viewScale', 'week') as CalendarViewScale) || 'week';
    } catch {
      return 'week';
    }
  });
  const [timeResolution, setTimeResolution] = useState<TimeResolution>(() => {
    try {
      return (getPreference('timesheet.calendar.timeResolution', '15-min') as TimeResolution) || '15-min';
    } catch {
      return '15-min';
    }
  });
  // Removed unused dragging state - using isDragging instead
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<any | null>(null);
  const [createFormData, setCreateFormData] = useState({
    companyId: '',
    accountId: '',
    taskId: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    description: ''
  });
  const [tasks, setTasks] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [dialogTasks, setDialogTasks] = useState<any[]>([]);
  const [isLoadingDialogData, setIsLoadingDialogData] = useState(false);
  const [weekPickerAnchorEl, setWeekPickerAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Timezone + 24h clock: derive from `preferences` so updates always apply (same parser as dateUtils tooltips)
  const timezone = getUserTimezone(preferences);
  const clock24h = useMemo(() => getUserClock24h(preferences), [preferences]);

  const { startDate, endDate } = useMemo(
    () => getStartEndDateStrings(currentDate, viewScale),
    [currentDate, viewScale]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await api.getProfile();
        const uid = profile?.data?.user?.id ?? profile?.data?.id ?? null;
        if (!cancelled) setCurrentUserId(uid);
      } catch (err) {
        console.error('Error loading profile for calendar scope:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadEntries = useCallback(async (showLoading = true) => {
    if (!organizationId || !currentUserId) return;
    try {
      if (showLoading) setEntriesLoading(true);
      setError(null);
      const response = await api.getTimesheetEntries({
        organizationId,
        startDate,
        endDate,
        timezone,
        userId: currentUserId
      });
      setEntries(response.data || []);
    } catch (err: any) {
      console.error('Error loading calendar entries:', err);
      setError(err.response?.data?.message || 'Failed to load timesheet entries');
      setEntries([]);
    } finally {
      if (showLoading) setEntriesLoading(false);
    }
  }, [organizationId, startDate, endDate, timezone, currentUserId]);

  useEffect(() => {
    if (!currentUserId) return;
    void loadEntries();
  }, [loadEntries, currentUserId]);

  // Generate time slots based on resolution
  const timeSlots = useMemo(() => {
    try {
      const resolution = TIME_RESOLUTIONS.find(r => r.value === timeResolution)?.minutes || 15;
      const slots: TimeSlot[] = [];
      const start = startOfDay(new Date());
      
      for (let hour = 0; hour < 24; hour++) {
        for (let min = 0; min < 60; min += resolution) {
          const time = setMinutes(setHours(start, hour), min);
          slots.push({
            time,
            displayTime: formatTime(time, clock24h)
          });
        }
      }
      
      return slots;
    } catch (err) {
      console.error('Error generating time slots:', err);
      // Return default slots if there's an error
      const slots: TimeSlot[] = [];
      for (let hour = 0; hour < 24; hour++) {
        slots.push({
          time: new Date(2024, 0, 1, hour, 0),
          displayTime: `${hour.toString().padStart(2, '0')}:00`
        });
      }
      return slots;
    }
  }, [timeResolution, clock24h]);

  // Calculate date range based on view scale (shared with Table view)
  const dateRange = useMemo(() => {
    try {
      return getDateRangeFromScale(currentDate, viewScale);
    } catch (err) {
      console.error('Error calculating date range:', err);
      return [currentDate];
    }
  }, [currentDate, viewScale]);

  // Per-day totals: split each entry into local-day segments so midnight-spanning entries
  // contribute to the correct day column.
  const dayTotals = useMemo(() => {
    type DayTotal = {
      totalMinutes: number;
      // Tooltip lines for segments in the format: `<description> - <hours>h`
      tooltipLines: string[];
    };

    const totalsByDayKey = new Map<LocalDateKey, DayTotal>();

    if (!dateRange || dateRange.length === 0) {
      return totalsByDayKey;
    }

    const visibleDayKeys: LocalDateKey[] = dateRange.map((d) => getLocalDateKey(d, timezone));
    for (const key of visibleDayKeys) {
      totalsByDayKey.set(key, { totalMinutes: 0, tooltipLines: [] });
    }

    for (const entry of entries || []) {
      if (!entry?.startTime || !entry?.stopTime) continue;

      const start = parseISO(entry.startTime);
      const stop = parseISO(entry.stopTime);
      if (!start || !stop) continue;
      if (stop <= start) continue;

      const startKey = getLocalDateKey(start, timezone);
      const stopKey = getLocalDateKey(stop, timezone);
      const startMinutes = getLocalMinutesFromMidnight(start, timezone);
      const stopMinutes = getLocalMinutesFromMidnight(stop, timezone);

      const description =
        (typeof entry.description === 'string' && entry.description.trim() !== '' && entry.description.trim()) ||
        entry?.task?.name ||
        'No description';

      const addSegmentToDay = (dayKey: LocalDateKey, minutes: number) => {
        if (!totalsByDayKey.has(dayKey)) return;
        if (minutes <= 0) return;
        const hours = minutes / 60;
        const dayTotal = totalsByDayKey.get(dayKey)!;
        dayTotal.totalMinutes += minutes;
        dayTotal.tooltipLines.push(`${description} - ${hours.toFixed(2)}h`);
      };

      // Same local day: allocate full duration to that day.
      if (startKey === stopKey) {
        const minutes = Math.max(0, differenceInMinutes(stop, start));
        addSegmentToDay(startKey, minutes);
        continue;
      }

      // Different local days: allocate remaining minutes of the start day, full intermediate days,
      // and initial minutes of the stop day.
      addSegmentToDay(startKey, 1440 - startMinutes);
      addSegmentToDay(stopKey, stopMinutes);

      // Intermediate days (if any)
      const startUTCDate = localDateKeyToUTCDate(startKey);
      const stopUTCDate = localDateKeyToUTCDate(stopKey);
      if (startUTCDate.getTime() < stopUTCDate.getTime()) {
        let cursor = addDays(startUTCDate, 1);
        while (cursor.getTime() < stopUTCDate.getTime()) {
          const cursorKey = utcDateToLocalDateKey(cursor);
          addSegmentToDay(cursorKey, 1440);
          cursor = addDays(cursor, 1);
        }
      }
    }

    return totalsByDayKey;
  }, [entries, dateRange, timezone]);

  const entriesByDayKey = useMemo(() => {
    const map = new Map<LocalDateKey, any[]>();
    for (const entry of entries || []) {
      if (!entry?.startTime) continue;
      try {
        const key = getLocalDateKey(parseISO(entry.startTime), timezone);
        const list = map.get(key);
        if (list) list.push(entry);
        else map.set(key, [entry]);
      } catch {
        // ignore bad timestamps
      }
    }
    return map;
  }, [entries, timezone]);

  const layoutsByDayKey = useMemo(() => {
    const map = new Map<LocalDateKey, CalendarEntryBlock[]>();
    const slotCount = timeSlots?.length || 0;
    for (const day of dateRange || []) {
      const key = getLocalDateKey(day, timezone);
      const dayEntries = entriesByDayKey.get(key) || [];
      map.set(key, calculateOverlapLayout(dayEntries, slotCount, timezone, timeResolution));
    }
    return map;
  }, [dateRange, entriesByDayKey, timeSlots, timezone, timeResolution]);

  // Load tasks
  useEffect(() => {
    const loadTasks = async () => {
      try {
        setError(null);
        const [accountsResponse, tasksResponse] = await Promise.all([
          api.getAccounts({ organizationId }),
          api.getTasks({ organizationId })
        ]);
        const accounts = accountsResponse.data || [];
        const accountsById = new Map(accounts.map((a: any) => [a.id, a]));
        const allTasks = (tasksResponse.data || []).map((task: any) => ({
          ...task,
          account: task.account || accountsById.get(task.accountId) || null
        }));
        setTasks(allTasks);
      } catch (err: any) {
        console.error('Error loading tasks:', err);
        setError(err.response?.data?.message || 'Failed to load tasks');
      }
    };

    if (organizationId) {
      loadTasks();
    } else {
      setTasks([]);
    }
  }, [organizationId]);

  // Persist calendar prefs (skip unchanged via context; stable setPreference)
  useEffect(() => {
    if (!isMobile) {
      void setPreference('timesheet.calendar.viewScale', viewScale, 'string');
    }
  }, [viewScale, isMobile, setPreference]);

  useEffect(() => {
    void setPreference('timesheet.calendar.timeResolution', timeResolution, 'string');
  }, [timeResolution, setPreference]);


  // Handle mouse down for drag-to-create (desktop/tablet only)
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartSlot, setDragStartSlot] = useState<{ day: Date; slot: number } | null>(null);
  const [dragCurrentSlot, setDragCurrentSlot] = useState<{ day: Date; slot: number } | null>(null);
  const [hasMoved, setHasMoved] = useState(false);
  const dragRafRef = useRef<number | null>(null);
  const dragCurrentRef = useRef<{ day: Date; slot: number } | null>(null);

  const slotFromPointer = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const y = Math.max(0, event.clientY - rect.top);
      const slotCount = timeSlots?.length || 1;
      return Math.min(slotCount - 1, Math.floor(y / SLOT_HEIGHT));
    },
    [timeSlots]
  );

  const handleMouseDown = (day: Date, event: React.MouseEvent<HTMLElement>) => {
    if (isMobile) return;
    event.preventDefault();
    const slot = slotFromPointer(event);
    setIsDragging(true);
    setHasMoved(false);
    const point = { day, slot };
    setDragStartSlot(point);
    setDragCurrentSlot(point);
    dragCurrentRef.current = point;
  };

  const handleMouseMove = (day: Date, event: React.MouseEvent<HTMLElement>) => {
    if (!isDragging || !dragStartSlot || isMobile) return;
    const slot = slotFromPointer(event);
    const point = { day, slot };
    dragCurrentRef.current = point;
    setHasMoved(true);
    if (dragRafRef.current != null) return;
    dragRafRef.current = window.requestAnimationFrame(() => {
      dragRafRef.current = null;
      const next = dragCurrentRef.current;
      if (!next) return;
      setDragCurrentSlot((prev) => {
        if (prev && prev.day.getTime() === next.day.getTime() && prev.slot === next.slot) {
          return prev;
        }
        return next;
      });
    });
  };

  const handleMouseUp = () => {
    if (!isDragging || !dragStartSlot || !dragCurrentSlot || isMobile) return;

    const startSlot = Math.min(dragStartSlot.slot, dragCurrentSlot.slot);
    const endSlot = Math.max(dragStartSlot.slot, dragCurrentSlot.slot);
    const day = dragStartSlot.day;

    // If user just clicked (didn't drag), open dialog for single slot
    if (!hasMoved || startSlot === endSlot) {
      const dayStart = startOfDay(day);
      const resolution = TIME_RESOLUTIONS.find(r => r.value === timeResolution)!.minutes;
      const startTime = addMinutes(dayStart, startSlot * resolution);
      const endTime = addMinutes(startTime, 60); // Default 1 hour

      setCreateFormData({
        companyId: '',
        accountId: '',
        taskId: tasks.length > 0 ? tasks[0].id : '',
        startDate: format(startTime, 'yyyy-MM-dd'),
        startTime: format(startTime, 'HH:mm'),
        endDate: format(endTime, 'yyyy-MM-dd'),
        endTime: format(endTime, 'HH:mm'),
        description: ''
      });
      setEditingEntry(null);
      setCreateDialogOpen(true);
      loadDialogData();
    } else {
      // User dragged - create entry with dragged range
      const dayStart = startOfDay(day);
      const startTime = addMinutes(dayStart, startSlot * TIME_RESOLUTIONS.find(r => r.value === timeResolution)!.minutes);
      const endTime = addMinutes(dayStart, endSlot * TIME_RESOLUTIONS.find(r => r.value === timeResolution)!.minutes);

      setCreateFormData({
        companyId: '',
        accountId: '',
        taskId: tasks.length > 0 ? tasks[0].id : '',
        startDate: format(startTime, 'yyyy-MM-dd'),
        startTime: format(startTime, 'HH:mm'), // HTML5 time input requires 24-hour format
        endDate: format(endTime, 'yyyy-MM-dd'),
        endTime: format(endTime, 'HH:mm'), // HTML5 time input requires 24-hour format
        description: ''
      });
      setEditingEntry(null);
      setCreateDialogOpen(true);
      loadDialogData();
    }

    setIsDragging(false);
    setDragStartSlot(null);
    setDragCurrentSlot(null);
    setHasMoved(false);
  };

  // Clean up mouse events
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseUp = () => handleMouseUp();
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [isDragging, dragStartSlot, dragCurrentSlot, hasMoved, tasks, timeResolution, clock24h]);

  // Handle tap to create (mobile)
  const handleTimeSlotClick = (day: Date, slot: number) => {
    if (!isMobile) return;

    // Combine day date with slot time
    const dayStart = startOfDay(day);
    const resolution = TIME_RESOLUTIONS.find(r => r.value === timeResolution)!.minutes;
    const startTime = addMinutes(dayStart, slot * resolution);
    const endTime = addMinutes(startTime, 60); // Default 1 hour

    setCreateFormData({
      companyId: '',
      accountId: '',
      taskId: tasks[0]?.id || '',
      startDate: format(startTime, 'yyyy-MM-dd'),
      startTime: format(startTime, 'HH:mm'), // HTML5 time input requires 24-hour format
      endDate: format(endTime, 'yyyy-MM-dd'),
      endTime: format(endTime, 'HH:mm'), // HTML5 time input requires 24-hour format
      description: ''
    });
    setEditingEntry(null);
    setCreateDialogOpen(true);
    loadDialogData();
  };

  // Handle entry click to edit
  const handleEntryClick = (entry: any) => {
    if (!entry.startTime || !entry.stopTime) return;
    const startFields = utcIsoToFormFields(entry.startTime, timezone);
    const stopFields = utcIsoToFormFields(entry.stopTime, timezone);
    
    setCreateFormData({
      companyId: entry.task?.account?.company?.id || '',
      accountId: entry.task?.account?.id || '',
      taskId: entry.taskId || '',
      startDate: startFields.date,
      startTime: startFields.time,
      endDate: stopFields.date,
      endTime: stopFields.time,
      description: entry.description || ''
    });
    setEditingEntry(entry);
    setCreateDialogOpen(true);
    loadDialogData();
  };

  // Load dialog data (companies, accounts, tasks) - organization is already set from title bar
  const loadDialogData = async () => {
    if (!organizationId) return;
    
    setIsLoadingDialogData(true);
    try {
      // Load companies for the active organization
      const companiesResponse = await api.getCompanies({ organizationId });
      const comps = (companiesResponse.data || []).filter((c: any) => c.isActive !== false || editingEntry);
      setCompanies(comps);
      
      // Auto-select if only one company
      if (comps.length === 1 && !editingEntry && !createFormData.companyId) {
        setCreateFormData(prev => ({ ...prev, companyId: comps[0].id }));
        await loadAccounts(comps[0].id);
      } else if (createFormData.companyId) {
        await loadAccounts(createFormData.companyId);
      }
    } catch (err: any) {
      console.error('Error loading dialog data:', err);
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setIsLoadingDialogData(false);
    }
  };

  // Load data when dialog opens
  useEffect(() => {
    if (createDialogOpen && !isLoadingDialogData) {
      loadDialogData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createDialogOpen]);

  // Load accounts for a company
  const loadAccounts = async (companyId: string) => {
    if (!organizationId) return;
    
    try {
      const accountsResponse = await api.getAccounts({ organizationId, companyId });
      const accs = (accountsResponse.data || []).filter((a: any) => a.isActive !== false);
      setAccounts(accs);
      
      // Auto-select if only one account
      if (accs.length === 1 && !editingEntry && !createFormData.accountId) {
        setCreateFormData(prev => ({ ...prev, accountId: accs[0].id }));
        await loadDialogTasks(accs[0].id);
      } else if (createFormData.accountId) {
        await loadDialogTasks(createFormData.accountId);
      }
    } catch (err: any) {
      console.error('Error loading accounts:', err);
    }
  };

  // Load tasks for an account
  const loadDialogTasks = async (accountId: string) => {
    if (!organizationId) return;
    
    try {
      const tasksResponse = await api.getTasks({ accountId, organizationId });
      const tks = (tasksResponse.data || []).filter((t: any) => t.isActive !== false);
      setDialogTasks(tks);
      
      // Auto-select if only one task
      if (tks.length === 1 && !editingEntry && !createFormData.taskId) {
        setCreateFormData(prev => ({ ...prev, taskId: tks[0].id }));
      }
    } catch (err: any) {
      console.error('Error loading tasks:', err);
    }
  };

  // Calculate hours from start/end times
  const calculateHours = useMemo(() => {
    if (!createFormData.startDate || !createFormData.startTime || !createFormData.endDate || !createFormData.endTime) {
      return 0;
    }
    
    try {
      const start = formFieldsToUtcDate(createFormData.startDate, createFormData.startTime, timezone);
      const end = formFieldsToUtcDate(createFormData.endDate, createFormData.endTime, timezone);
      if (!start || !end) return 0;
      
      if (end <= start) return 0;
      
      const diffMs = end.getTime() - start.getTime();
      return diffMs / (1000 * 60 * 60); // Convert to hours
    } catch {
      return 0;
    }
  }, [createFormData.startDate, createFormData.startTime, createFormData.endDate, createFormData.endTime, timezone]);

  // Handle company change
  const handleCompanyChange = async (companyId: string) => {
    setCreateFormData(prev => ({
      ...prev,
      companyId,
      accountId: '',
      taskId: ''
    }));
    setAccounts([]);
    setDialogTasks([]);
    
    if (companyId && organizationId) {
      await loadAccounts(companyId);
    }
  };

  // Handle account change
  const handleAccountChange = async (accountId: string) => {
    setCreateFormData(prev => ({
      ...prev,
      accountId,
      taskId: ''
    }));
    setDialogTasks([]);
    
    if (accountId && organizationId) {
      await loadDialogTasks(accountId);
    }
  };

  // Handle create/update entry
  const handleCreateEntry = async () => {
    if (!onEntryCreate || !createFormData.taskId || !createFormData.startDate || !createFormData.startTime || !createFormData.endDate || !createFormData.endTime) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      // Convert preference-timezone date/time input into UTC for storage.
      const startDateTime = formFieldsToUtcDate(createFormData.startDate, createFormData.startTime, timezone);
      const endDateTime = formFieldsToUtcDate(createFormData.endDate, createFormData.endTime, timezone);
      if (!startDateTime || !endDateTime) {
        alert('Invalid date/time.');
        return;
      }
      
      if (endDateTime <= startDateTime) {
        alert('End time must be after start time.');
        return;
      }
      
      const entryData = {
        organizationId: organizationId,
        taskId: createFormData.taskId,
        startTime: startDateTime.toISOString(),
        stopTime: endDateTime.toISOString(),
        description: createFormData.description,
        approvalStatus: 'draft'
      };
      
      savedScrollTopRef.current = calendarScrollRef.current?.scrollTop ?? 0;
      if (editingEntry && onEntryUpdate) {
        await onEntryUpdate(editingEntry.id, entryData);
      } else if (onEntryCreate) {
        await onEntryCreate(entryData);
      }
      await loadEntries(false);
      
      setCreateDialogOpen(false);
      setEditingEntry(null);
      setCreateFormData({
        companyId: '',
        accountId: '',
        taskId: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        description: ''
      });
    } catch (err: any) {
      console.error('Error saving entry:', err);
      alert(err.response?.data?.message || 'Failed to save entry');
    }
  };

  const handleDeleteEntry = async () => {
    if (!editingEntry || !onEntryDelete) return;
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return;
    try {
      savedScrollTopRef.current = calendarScrollRef.current?.scrollTop ?? 0;
      await onEntryDelete(editingEntry.id);
      await loadEntries(false);
      setCreateDialogOpen(false);
      setEditingEntry(null);
      setCreateFormData({
        companyId: '',
        accountId: '',
        taskId: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        description: ''
      });
    } catch (err: any) {
      console.error('Error deleting entry:', err);
      alert(err.response?.data?.message || 'Failed to delete entry');
    }
  };

  const calendarScrollRef = useRef<HTMLDivElement>(null);
  const savedScrollTopRef = useRef<number | null>(null);
  const hasInitializedScrollRef = useRef(false);

  const handleJumpToNow = () => {
    setCurrentDate(new Date());
    setTimeout(() => {
      if (calendarScrollRef.current && timeSlots && timeSlots.length > 0) {
        const now = new Date();
        const resolution = TIME_RESOLUTIONS.find(r => r.value === timeResolution)?.minutes || 15;
        const currentMinutesFromMidnight = now.getHours() * 60 + now.getMinutes();
        const currentSlot = Math.floor(currentMinutesFromMidnight / resolution);
        const TOP_PADDING = SLOT_HEIGHT / 2;
        const y = currentSlot * SLOT_HEIGHT + TOP_PADDING;
        const scrollContainer = calendarScrollRef.current;
        const scrollPosition = y - (scrollContainer.clientHeight / 2);
        scrollContainer.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // All hooks must be before early returns
  // Current time state for real-time updates
  const [currentTime, setCurrentTime] = useState(new Date());
  /** Width of vertical scrollbar on grid scroll container; pad header/totals so column lines align. */
  const [gridScrollbarWidth, setGridScrollbarWidth] = useState(0);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  useLayoutEffect(() => {
    const el = calendarScrollRef.current;
    if (!el) {
      setGridScrollbarWidth(0);
      return;
    }
    const measure = () => {
      // Classic scrollbars: difference is track width. Clamp avoids bogus values from subpixel/layout quirks.
      const w = el.offsetWidth - el.clientWidth;
      setGridScrollbarWidth(Math.max(0, Math.min(Math.round(w), 24)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    el.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, [entriesLoading, error, entries, dateRange, timeSlots, timeResolution]);

  // Restore scroll position after create/update/delete
  useEffect(() => {
    if (savedScrollTopRef.current !== null && calendarScrollRef.current) {
      const top = savedScrollTopRef.current;
      savedScrollTopRef.current = null;
      requestAnimationFrame(() => {
        if (calendarScrollRef.current) {
          calendarScrollRef.current.scrollTop = top;
        }
      });
    }
  }, [entries]);

  // Initialize scroll position when entering the calendar route (default 07:00).
  // Runs only once per component mount; switching calendar days preserves scroll position.
  useEffect(() => {
    if (hasInitializedScrollRef.current) return;
    if (!calendarScrollRef.current) return;
    if (!dateRange || dateRange.length === 0) return;

    const earliestMinutes = 7 * 60;

    const resolution = TIME_RESOLUTIONS.find(r => r.value === timeResolution)?.minutes || 15;
    const targetSlot = Math.floor(earliestMinutes / resolution);
    const TOP_PADDING = SLOT_HEIGHT / 2;
    const y = targetSlot * SLOT_HEIGHT + TOP_PADDING;

    const scrollContainer = calendarScrollRef.current!;
    const scrollPosition = y - scrollContainer.clientHeight / 2;
    scrollContainer.scrollTo({
      top: Math.max(0, scrollPosition),
      behavior: 'auto'
    });

    hasInitializedScrollRef.current = true;
  }, [dateRange, timeResolution]);

  // Check if we're viewing the current week
  const isCurrentWeek = useMemo(() => {
    if (!dateRange || dateRange.length === 0) return false;
    const today = new Date();
    return dateRange.some(day => isSameDay(day, today));
  }, [dateRange]);

  // Calculate current time position
  const currentTimePosition = useMemo(() => {
    if (!isCurrentWeek || !timeSlots || timeSlots.length === 0) return null;
    
    const resolution = TIME_RESOLUTIONS.find(r => r.value === timeResolution)?.minutes || 15;
    const currentMinutesFromMidnight = currentTime.getHours() * 60 + currentTime.getMinutes();
    const currentSlot = Math.floor(currentMinutesFromMidnight / resolution);
    const TOP_PADDING = SLOT_HEIGHT / 2; // Half slot for alignment
    const y = currentSlot * SLOT_HEIGHT + TOP_PADDING;
    
    return { y, time: formatTime(currentTime, clock24h) };
  }, [isCurrentWeek, timeSlots, timeResolution, currentTime, clock24h]);

  // Error boundary - if there's an error, show it
  if (error) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <Typography variant="h6" color="error">
          Error loading calendar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  // Wait for entries and shared preferences (clock24h defaults to 12h until first fetch completes)
  if (entriesLoading || preferencesLoading || !preferencesHydrated) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading calendar...
        </Typography>
      </Box>
    );
  }

  // Safety check - ensure we have valid data
  if (!timeSlots || timeSlots.length === 0 || !dateRange || dateRange.length === 0) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Initializing calendar...
        </Typography>
      </Box>
    );
  }

  // Safety check - ensure we have valid data before rendering
  if (!timeSlots || timeSlots.length === 0) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Initializing calendar...
        </Typography>
      </Box>
    );
  }

  if (!dateRange || dateRange.length === 0) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Calculating date range...
        </Typography>
      </Box>
    );
  }


  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
        <TimesheetViewControls
          currentDate={currentDate}
          viewScale={viewScale}
          onDateChange={setCurrentDate}
          onViewScaleChange={setViewScale}
          onJumpToNow={handleJumpToNow}
          showResolution
          timeResolution={timeResolution}
          onTimeResolutionChange={setTimeResolution}
        />

      {/* Calendar grid: header + scrollable hour area + fixed totals row (totals sit above app footer, not sticky inside scroll). */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          bgcolor: 'background.default',
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        {/* Row 1: day headers; trailing spacer matches vertical scrollbar width so day columns match grid */}
        <Box sx={{ display: 'flex', flexShrink: 0, width: '100%', alignItems: 'stretch' }}>
          <Box
            sx={{
              width: 80,
              flexShrink: 0,
              height: 60,
              borderRight: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.default'
            }}
          />
          {dateRange.map((day, dayIndex) => (
            <Box
              key={`hdr-${dayIndex}`}
              sx={{
                flex: 1,
                minWidth: 0,
                height: 60,
                borderBottom: 1,
                borderRight: dayIndex < dateRange.length - 1 ? 1 : 0,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isSameDay(day, new Date()) ? 'primary.light' : 'background.paper'
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {format(day, 'EEE')}
              </Typography>
              <Typography variant="h6">{format(day, 'd')}</Typography>
            </Box>
          ))}
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: gridScrollbarWidth,
              minWidth: gridScrollbarWidth,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          />
        </Box>

        {/* Row 2: only the hour grid scrolls */}
        <Box
          ref={calendarScrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            position: 'relative',
          }}
        >
          <Box sx={{ display: 'flex', width: '100%' }}>
            {/* Time labels column */}
            <Box sx={{ width: 80, flexShrink: 0, borderRight: 1, borderColor: 'divider' }}>
              {timeSlots && timeSlots.length > 0 && timeSlots.map((slot, index) => (
                <Box
                  key={index}
                  sx={{
                    height: SLOT_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    pr: 1,
                    fontSize: '0.75rem',
                    color: 'text.secondary'
                  }}
                >
                  {(() => {
                    const res = TIME_RESOLUTIONS.find(r => r.value === timeResolution);
                    const interval = res ? 60 / res.minutes : 4;
                    if (index % interval === 0) {
                      return slot.displayTime;
                    }
                    return null;
                  })()}
                </Box>
              ))}
            </Box>

            {/* Day columns: sparse hour grid + pointer hit layer + entries */}
            {dateRange.map((day, dayIndex) => {
              const dayKey = getLocalDateKey(day, timezone);
              const dayBlocks = layoutsByDayKey.get(dayKey) || [];
              const maxColumns = dayBlocks.length > 0 ? Math.max(1, ...dayBlocks.map(b => b.column + 1)) : 1;
              const resolution = TIME_RESOLUTIONS.find(r => r.value === timeResolution)?.minutes || 15;
              const slotCount = timeSlots?.length || 0;
              const columnHeight = slotCount * SLOT_HEIGHT;
              const dragOnThisDay =
                isDragging &&
                dragStartSlot &&
                dragCurrentSlot &&
                dragStartSlot.day.getTime() === day.getTime();
              const dragMin = dragOnThisDay
                ? Math.min(dragStartSlot!.slot, dragCurrentSlot!.slot)
                : 0;
              const dragMax = dragOnThisDay
                ? Math.max(dragStartSlot!.slot, dragCurrentSlot!.slot)
                : 0;

              return (
                <Box
                  key={dayKey}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRight: dayIndex < dateRange.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                    position: 'relative'
                  }}
                >
                  <Box sx={{ position: 'relative', height: columnHeight }}>
                    {/* Current Time Indicator - only show on today's column in current week */}
                    {isCurrentWeek && isSameDay(day, new Date()) && currentTimePosition && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -4,
                          top: currentTimePosition.y - 1,
                          width: `calc(100% + 8px)`,
                          zIndex: 100,
                          pointerEvents: 'none'
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: -4,
                            top: -4,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'red',
                            border: '1px solid',
                            borderColor: 'darkred'
                          }}
                        />
                        <Box
                          sx={{
                            height: 2,
                            bgcolor: 'red',
                            opacity: 0.8,
                            width: '100%'
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            left: -50,
                            top: -8,
                            bgcolor: 'white',
                            px: 0.5,
                            borderRadius: 0.5,
                            fontSize: '0.625rem',
                            fontWeight: 'bold',
                            color: 'red'
                          }}
                        >
                          {currentTimePosition.time}
                        </Box>
                      </Box>
                    )}

                    {/* Hour grid lines only (not per-slot interactive nodes) */}
                    {Array.from({ length: 24 }, (_, hour) => (
                      <Box
                        key={`hour-${hour}`}
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: ((hour * 60) / resolution) * SLOT_HEIGHT,
                          height: (60 / resolution) * SLOT_HEIGHT,
                          boxSizing: 'border-box',
                          borderBottom: 2,
                          borderColor: 'text.disabled',
                          pointerEvents: 'none'
                        }}
                      />
                    ))}

                    {/* Drag highlight */}
                    {dragOnThisDay && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: dragMin * SLOT_HEIGHT,
                          height: (dragMax - dragMin + 1) * SLOT_HEIGHT,
                          bgcolor: 'action.selected',
                          pointerEvents: 'none',
                          zIndex: 1
                        }}
                      />
                    )}

                    {/* Single hit layer for create/drag */}
                    <Box
                      onMouseDown={(e) => handleMouseDown(day, e)}
                      onMouseMove={(e) => handleMouseMove(day, e)}
                      onClick={(e) => {
                        if (!isMobile) return;
                        handleTimeSlotClick(day, slotFromPointer(e));
                      }}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 2,
                        cursor: isMobile ? 'pointer' : 'crosshair',
                        bgcolor: 'transparent'
                      }}
                    />

                    {/* Entry Blocks */}
                    {dayBlocks.map((block) => {
                      const width = 100 / maxColumns;
                      const left = (block.column * width);
                      const top = block.startSlot * SLOT_HEIGHT;
                      const height = (block.endSlot - block.startSlot) * SLOT_HEIGHT;
                      
                      // Determine entry size category
                      // Use height and approximate width percentage (assuming typical column width)
                      // For a 7-day week, each column is ~14% width, so 80px threshold is roughly 5-6% at 1200px viewport
                      // We'll use a conservative estimate: if width > 8% and height >= 40px, it's large
                      // Ensure the "large"/"medium"/"small" rendering branches are mutually exclusive.
                      // Otherwise large entries also satisfy the medium threshold and render the metadata line twice.
                      const isLarge = height >= 40 && width >= 8;
                      const isMedium = !isLarge && height >= 30 && width >= 8;
                      const isSmall = !isLarge && !isMedium;
                      
                      const textColor = getContrastColor(block.color);
                      const metadataLine = buildMetadataLine(block.entry);
                      
                      // Build tooltip content
                      const startTime = block.entry.startTime ? parseISO(block.entry.startTime) : null;
                      const stopTime = block.entry.stopTime ? parseISO(block.entry.stopTime) : null;
                      const duration = startTime && stopTime ? differenceInMinutes(stopTime, startTime) : 0;
                      const hours = Math.floor(duration / 60);
                      const minutes = duration % 60;
                      const durationStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                      
                      const startTimeStr = startTime ? formatTimeOnly(startTime.toISOString(), preferences) : null;
                      const stopTimeStr = stopTime ? formatTimeOnly(stopTime.toISOString(), preferences) : null;
                      const startDateStr = startTime ? format(startTime, 'yyyy-MM-dd') : null;
                      const stopDateStr = stopTime ? format(stopTime, 'yyyy-MM-dd') : null;
                      
                      const tooltipContent = [
                        `Project: ${block.entry.task?.name || 'Unknown'}`,
                        `Description: ${block.entry.description || 'No description'}`,
                        block.entry.organization?.name ? `Organization: ${block.entry.organization.name}` : null,
                        block.entry.task?.account?.name ? `Account: ${block.entry.task.account.name}` : null,
                        block.entry.task?.account?.company?.name ? `Company: ${block.entry.task.account.company.name}` : null,
                        startTime ? `Start: ${startDateStr} ${startTimeStr}` : null,
                        stopTime ? `End: ${stopDateStr} ${stopTimeStr}` : null,
                        `Duration: ${durationStr}`
                      ].filter(Boolean).join('\n');

                      return (
                        <Tooltip
                          key={block.entry.id}
                          title={tooltipContent}
                          arrow
                          placement="top"
                        >
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEntryClick(block.entry);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            sx={{
                              position: 'absolute',
                              left: `${left}%`,
                              width: `${width}%`,
                              top,
                              height,
                              bgcolor: block.color,
                              color: textColor,
                              p: 0.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'rgba(0, 0, 0, 0.1)',
                              cursor: 'pointer',
                              overflow: 'hidden',
                              display: 'flex',
                              flexDirection: 'column',
                              zIndex: 3,
                              '&:hover': {
                                opacity: 0.9,
                                zIndex: 10,
                                borderColor: 'rgba(0, 0, 0, 0.3)'
                              }
                            }}
                          >
                            {isLarge && block.entry.description && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  lineHeight: 1.2,
                                  mb: 0.25,
                                  wordWrap: 'break-word'
                                }}
                              >
                                {block.entry.description}
                              </Typography>
                            )}
                            {isLarge && (block.entry.description?.trim() !== metadataLine.trim()) && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 500,
                                  lineHeight: 1.2,
                                  wordWrap: 'break-word'
                                }}
                              >
                                {metadataLine}
                              </Typography>
                            )}
                            {isMedium && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 500,
                                  lineHeight: 1.2,
                                  wordWrap: 'break-word'
                                }}
                              >
                                {metadataLine}
                              </Typography>
                            )}
                            {isSmall && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 500,
                                  lineHeight: 1.2,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {block.entry.task?.name || 'Unknown'}
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Row 3: per-day totals; trailing spacer matches grid scrollbar */}
        <Box
          sx={{
            display: 'flex',
            flexShrink: 0,
            width: '100%',
            alignItems: 'stretch',
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              width: 80,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              py: 1.25
            }}
          />
          {dateRange.map((day, dayIndex) => {
            const dayKey = getLocalDateKey(day, timezone);
            const dayTotal = dayTotals.get(dayKey);
            const totalHours = (dayTotal?.totalMinutes ?? 0) / 60;
            const totalHoursLabel = `${totalHours.toFixed(2)}h`;
            const tooltipText = `Hours registered: ${totalHoursLabel}${dayTotal?.tooltipLines?.length ? `\n${dayTotal.tooltipLines.join('\n')}` : ''}`;
            const tooltipTitle = (
              <Box sx={{ whiteSpace: 'pre-line', maxWidth: 280 }}>
                {tooltipText}
              </Box>
            );
            return (
              <Box
                key={`tot-${dayIndex}`}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderRight: dayIndex < dateRange.length - 1 ? 1 : 0,
                  borderColor: 'divider',
                  py: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Tooltip title={tooltipTitle} arrow placement="top">
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      cursor: 'default'
                    }}
                  >
                    {totalHoursLabel}
                  </Typography>
                </Tooltip>
              </Box>
            );
          })}
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: gridScrollbarWidth,
              minWidth: gridScrollbarWidth,
              bgcolor: 'background.paper',
            }}
          />
        </Box>
      </Box>

      {/* Create/Edit Entry Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => {
          setCreateDialogOpen(false);
          setEditingEntry(null);
        }} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { width: 800, maxHeight: 600 }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: '4px 4px 0 0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}
            </Typography>
            {createFormData.startDate && (
              <Typography variant="body2">
                {format(new Date(createFormData.startDate), 'MMMM dd, yyyy')}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {isLoadingDialogData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Left Column: Time & Date */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Time & Date
                  </Typography>
                  
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={clock24h ? 'en-gb' : undefined}
                    key={clock24h ? 'clock-24' : 'clock-12'}
                  >
                    {/* Start Date & Time - Side by Side */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <TextField
                        label="Start Date"
                        type="date"
                        value={createFormData.startDate}
                        onChange={(e) => setCreateFormData({ ...createFormData, startDate: e.target.value })}
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                      />
                      <TimePicker
                        key={`start-time-${clock24h}`}
                        label="Start Time"
                        value={createFormData.startTime ? dayjs(`2000-01-01 ${createFormData.startTime}`) : null}
                        onChange={(newValue) => {
                          if (newValue) {
                            const timeStr = newValue.format('HH:mm');
                            setCreateFormData({ ...createFormData, startTime: timeStr });
                          }
                        }}
                        ampm={!clock24h}
                        format={clock24h ? 'HH:mm' : 'hh:mm A'}
                        slotProps={{
                          textField: {
                            size: 'small',
                            required: true,
                            InputLabelProps: { shrink: true },
                            sx: { 
                              width: clock24h ? 120 : 150,
                              minWidth: clock24h ? 120 : 150,
                              flexShrink: 0,
                              '& .MuiInputBase-input': {
                                padding: '8.5px 14px',
                                fontSize: '0.875rem'
                              }
                            }
                          },
                        }}
                      />
                    </Box>

                    {/* End Date & Time - Side by Side */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <TextField
                        label="End Date"
                        type="date"
                        value={createFormData.endDate}
                        onChange={(e) => setCreateFormData({ ...createFormData, endDate: e.target.value })}
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                      />
                      <TimePicker
                        key={`end-time-${clock24h}`}
                        label="End Time"
                        value={createFormData.endTime ? dayjs(`2000-01-01 ${createFormData.endTime}`) : null}
                        onChange={(newValue) => {
                          if (newValue) {
                            const timeStr = newValue.format('HH:mm');
                            setCreateFormData({ ...createFormData, endTime: timeStr });
                          }
                        }}
                        ampm={!clock24h}
                        format={clock24h ? 'HH:mm' : 'hh:mm A'}
                        slotProps={{
                          textField: {
                            size: 'small',
                            required: true,
                            InputLabelProps: { shrink: true },
                            sx: { 
                              width: clock24h ? 120 : 150,
                              minWidth: clock24h ? 120 : 150,
                              flexShrink: 0,
                              '& .MuiInputBase-input': {
                                padding: '8.5px 14px',
                                fontSize: '0.875rem'
                              }
                            }
                          },
                        }}
                      />
                    </Box>
                  </LocalizationProvider>
                  
                  {/* Hours (Read-only) */}
                  <TextField
                    label="Hours"
                    value={calculateHours.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    sx={{ bgcolor: 'action.disabledBackground' }}
                  />
                </Box>
              </Grid>
              
              {/* Right Column: Organization & Details */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Company & Details
                  </Typography>
                  
                  {/* Company */}
                  <FormControl fullWidth required disabled={!organizationId}>
                    <InputLabel>Company</InputLabel>
                    <MuiSelect
                      value={createFormData.companyId}
                      label="Company"
                      onChange={(e) => handleCompanyChange(e.target.value)}
                    >
                      {companies.map(company => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  </FormControl>
                  
                  {/* Account */}
                  <FormControl fullWidth required disabled={!createFormData.companyId}>
                    <InputLabel>Account</InputLabel>
                    <MuiSelect
                      value={createFormData.accountId}
                      label="Account"
                      onChange={(e) => handleAccountChange(e.target.value)}
                    >
                      {accounts.map(account => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.name}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  </FormControl>
                  
                  {/* Task */}
                  <FormControl fullWidth required disabled={!createFormData.accountId}>
                    <InputLabel>Task</InputLabel>
                    <MuiSelect
                      value={createFormData.taskId}
                      label="Task"
                      onChange={(e) => setCreateFormData({ ...createFormData, taskId: e.target.value })}
                    >
                      {dialogTasks.map(task => (
                        <MenuItem key={task.id} value={task.id}>
                          {task.name}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  </FormControl>
                  
                  {/* Description */}
                  <TextField
                    label="Description"
                    value={createFormData.description}
                    onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter description (optional)"
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          {editingEntry && onEntryDelete ? (
            <Button
              onClick={handleDeleteEntry}
              color="error"
              sx={{ mr: 'auto' }}
            >
              Delete
            </Button>
          ) : null}
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setEditingEntry(null);
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateEntry} 
            variant="contained" 
            disabled={
              !createFormData.taskId || 
              !createFormData.startDate || 
              !createFormData.startTime || 
              !createFormData.endDate || 
              !createFormData.endTime ||
              isLoadingDialogData
            }
          >
            {editingEntry ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimesheetCalendar;
